export const STAFF_ROLES = {
  Volunteer: 'volunteer',
  Manager: 'manager',
  Administrator: 'administrator',
} as const;

export type StaffRole =
  | typeof STAFF_ROLES.Volunteer
  | typeof STAFF_ROLES.Manager
  | typeof STAFF_ROLES.Administrator;

export enum StaffPermission {
  SeniorCheckIn = 'SeniorCheckIn',
  DeliverMeals = 'DeliverMeals',
  ReviewApplications = 'ReviewApplications',
  ReviewTransportation = 'ReviewTransportation',
  CreateApplicationOnBehalf = 'CreateApplicationOnBehalf',
  ManageStaff = 'ManageStaff',
  ManageLocations = 'ManageLocations',
}

const ROLE_RANK: Record<StaffRole, number> = {
  [STAFF_ROLES.Volunteer]: 1,
  [STAFF_ROLES.Manager]: 2,
  [STAFF_ROLES.Administrator]: 3,
};

const ROLE_PERMISSIONS: Record<StaffRole, ReadonlySet<StaffPermission>> = {
  [STAFF_ROLES.Volunteer]: new Set([
    StaffPermission.SeniorCheckIn,
    StaffPermission.DeliverMeals,
  ]),
  [STAFF_ROLES.Manager]: new Set([
    StaffPermission.SeniorCheckIn,
    StaffPermission.DeliverMeals,
    StaffPermission.ReviewApplications,
    StaffPermission.ReviewTransportation,
    StaffPermission.CreateApplicationOnBehalf,
  ]),
  [STAFF_ROLES.Administrator]: new Set([
    StaffPermission.SeniorCheckIn,
    StaffPermission.DeliverMeals,
    StaffPermission.ReviewApplications,
    StaffPermission.ReviewTransportation,
    StaffPermission.CreateApplicationOnBehalf,
    StaffPermission.ManageStaff,
    StaffPermission.ManageLocations,
  ]),
};

export function normalizeStaffRole(role?: string | null): StaffRole {
  const value = role?.trim().toLowerCase();

  if (!value || !(value in ROLE_RANK)) {
    return STAFF_ROLES.Volunteer;
  }

  return value as StaffRole;
}

export function hasStaffPermission(role: StaffRole, permission: StaffPermission): boolean {
  return ROLE_PERMISSIONS[role]?.has(permission) ?? false;
}

export function isManagerOrAbove(role: StaffRole): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[STAFF_ROLES.Manager];
}

export function isAdministrator(role: StaffRole): boolean {
  return role === STAFF_ROLES.Administrator;
}
